import { z } from 'zod';

import { BorrowerId, PortalUserId } from '../BrandedIds';
import { zodTemporalPlainDate } from '../Temporal';
import { zodBrandedString } from '../utils/Zod';
import { ExportableAddress } from './util/ExportableAddress';
import { ExportablePhone } from './util/ExportablePhone';
import { ExportableDayAndTimeRange } from './util/ExportableTime';
import { pii } from './util/pii';

export const ExportableFicoScore = z.int().min(300).max(850); // credit rating 300-850
export type ExportableFicoScore = z.infer<typeof ExportableFicoScore>;

export const ExportableCitizenshipStatus = z.enum([
  'usCitizen',
  'usNational',
  'lawfulPermanentResident',
  'conditionalPermanentResident',
  'refugee',
  'asylee',
  'unknown',
]);
export type ExportableCitizenshipStatus = z.infer<typeof ExportableCitizenshipStatus>;

export const ExportableGender = z.enum(['male', 'female', 'nonBinary', 'preferNotToSay', 'other']);
export type ExportableGender = z.infer<typeof ExportableGender>;

export const ExportableEthnicity = z.enum([
  'white',
  'black',
  'asian',
  'hispanic',
  'nativeAmerican',
  'pacificIslander',
  'middleEastern',
  'multiRacial',
  'other',
  'preferNotToSay',
]);
export type ExportableEthnicity = z.infer<typeof ExportableEthnicity>;

export const ExportableBorrowerName = z.strictObject({
  firstName: pii(z.string()),
  middleName: pii(z.string().optional()),
  lastName: pii(z.string().optional()),
  suffix: z.string().optional(),
});
export type ExportableBorrowerName = z.infer<typeof ExportableBorrowerName>;

export const ExportableBorrowerPreferences = z.object({ contactTimes: z.array(ExportableDayAndTimeRange).optional() });
export type ExportableBorrowerPreferences = z.infer<typeof ExportableBorrowerPreferences>;

export const ExportableBorrowerCompanySettings = z.object({
  emailConsent: z.boolean(),
  phoneConsent: z.boolean(),
  emailNotificationsEnabled: z.boolean(),
  paperNotificationsEnabled: z.boolean(),
  mailingAddress: ExportableAddress.optional(),
  preferences: ExportableBorrowerPreferences.optional(),
  citizenshipStatus: ExportableCitizenshipStatus.optional(),
  scraFlag: z.boolean().optional(), // Servicemembers Civil Relief Act (SCRA). Indicates borrower is military and has extra loan protections in cases of financial hardship
  vip: z.boolean().optional(), // company flag to pay extra attention to borrower
  ficoScore: ExportableFicoScore.optional(),
  dob: pii(zodTemporalPlainDate.optional()),
  gender: ExportableGender.optional(),
  ethnicity: ExportableEthnicity.optional(),
});
export type ExportableBorrowerCompanySettings = z.infer<typeof ExportableBorrowerCompanySettings>;

export const ExportableBaseBorrowerAndPortalUser = z.object({
  phoneNumbers: ExportablePhone,
  name: pii(z.string()),
  raw: ExportableBorrowerName,
});
export type ExportableBaseBorrowerAndPortalUser = z.infer<typeof ExportableBaseBorrowerAndPortalUser>;

// TODO: Discuss whether SSN (or last-4) should be included on ExportableBorrower.
// SSN is encrypted at rest (Bytes column with companyDecrypt) so adding it here
// would require decryption in the resource watcher publisher. For now, reports
// that need SSN data bypass the exportable model and use the borrowerSSN
// DataLoader directly.
export const ExportableBorrower = ExportableBaseBorrowerAndPortalUser.extend({
  id: zodBrandedString<BorrowerId>(),
  portalUserId: zodBrandedString<PortalUserId>().optional(),
  email: pii(z.string().optional()),
  settings: ExportableBorrowerCompanySettings,
}).meta({ title: 'Borrower' });
export type ExportableBorrower = z.infer<typeof ExportableBorrower>;

const ExportableBorrowerEmailConsentActor = z.union([
  z.literal('lender'),
  z.literal('borrower'),
  z.literal('internal'),
]);
export type ExportableBorrowerEmailConsentActor = z.infer<typeof ExportableBorrowerEmailConsentActor>;

export const ExportableBorrowerEmailConsent = z.object({
  emailConsent: z.boolean(),
  actor: ExportableBorrowerEmailConsentActor,
  borrowerIp: z.string().optional(),
});
export type ExportableBorrowerEmailConsent = z.infer<typeof ExportableBorrowerEmailConsent>;

export const DEFAULT_PUBLIC_BORROWER_EMAIL_CONSENT: ExportableBorrowerEmailConsent = {
  emailConsent: true,
  actor: 'internal',
} as const;
